import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Scan, MapPin, Package, Clock, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CountSession {
  id: string;
  name: string;
  description: string;
  site: string;
  status: 'pending' | 'active' | 'completed' | 'reopened';
  created_at: string;
  items_count: number;
  completed_count: number;
  start_location: string | null;
  end_location: string | null;
  location_count: number;
}

const MobileScanner: React.FC = () => {
  const [sessions, setSessions] = useState<CountSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('count_sessions')
        .select(`
          *,
          count_items (
            id,
            status,
            location
          )
        `)
        .in('status', ['active', 'reopened'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedSessions = data.map(session => {
        const locations = session.count_items.map((item: any) => item.location);
        const uniqueLocations = Array.from(new Set(locations)).sort();
        
        return {
          ...session,
          items_count: session.count_items.length,
          completed_count: session.count_items.filter((item: any) => item.status === 'completed').length,
          start_location: uniqueLocations.length > 0 ? uniqueLocations[0] : null,
          end_location: uniqueLocations.length > 0 ? uniqueLocations[uniqueLocations.length - 1] : null,
          location_count: uniqueLocations.length
        };
      });

      setSessions(formattedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'badge-warning';
      case 'active':
        return 'badge-info';
      case 'completed':
        return 'badge-success';
      case 'reopened':
        return 'badge-info';
      default:
        return 'badge-ghost';
    }
  };

  const getProgressPercentage = (completed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {sessions.length === 0 ? (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body text-center">
            <Package className="w-16 h-16 mx-auto text-base-content/30 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Active Count Sessions</h2>
            <p className="text-base-content/70">
              There are no active count sessions available. Contact your administrator to start a count session.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((session) => (
            <div key={session.id} className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <div className="flex justify-between items-start mb-3">
                  <h2 className="card-title text-lg">{session.name}</h2>
                  <span className={`badge ${getStatusColor(session.status)}`}>
                    {session.status}
                  </span>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span>{session.site}</span>
                  </div>
                  
                  {session.start_location && session.end_location && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-accent" />
                      <span>
                        {session.start_location === session.end_location 
                          ? `Location: ${session.start_location}`
                          : `Locations: ${session.start_location} - ${session.end_location}`}
                        {session.location_count > 1 && ` (${session.location_count} locations)`}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-secondary" />
                    <span>{new Date(session.created_at).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="w-4 h-4 text-accent" />
                    <span>{session.items_count} items</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Progress</span>
                    <span>{getProgressPercentage(session.completed_count, session.items_count)}%</span>
                  </div>
                  <progress 
                    className="progress progress-primary w-full" 
                    value={session.completed_count} 
                    max={session.items_count}
                  ></progress>
                  <div className="text-xs text-base-content/70 mt-1">
                    {session.completed_count} of {session.items_count} completed
                  </div>
                </div>

                {session.description && (
                  <p className="text-sm text-base-content/70 mb-4">
                    {session.description}
                  </p>
                )}

                <div className="card-actions justify-end">
                  <Link 
                    to={`/count/${session.id}`}
                    className="btn btn-primary"
                  >
                    <Scan className="w-4 h-4 mr-2" />
                    Start Counting
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}


    </div>
  );
};

export default MobileScanner; 